import { useShipmentOptions } from "@/common/hooks/use-shipment-options.hook";
import {
  PassedDeviceContext,
  useReplacedDevice,
} from "@/pages/Replacement/hooks/use-replaced-device.hook";
import { useDeviceOptions } from "@/pages/Replacement/steps/ChooseDevice/hooks/use-device-options.hook";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import type { PickupBuilding } from "../choose-delivery.types";
import { toDeliveryChoices } from "../delivery-choices.mapper";
import { useShippingAddress } from "./use-shipping-address.hook";

export interface UseChooseDeliveryStepArgs {
  assetId: string;
  passedContext?: PassedDeviceContext;
}

/**
 * Loads ShipmentHound's options for the device chosen in step 1 and records the method and building in the
 * draft. The shipping address and its form belong to `useShippingAddress`.
 */
export const useChooseDeliveryStep = ({ assetId, passedContext }: UseChooseDeliveryStepArgs) => {
  const replacedDevice = useReplacedDevice(assetId, passedContext);
  const { device } = replacedDevice;
  const deviceOptions = useDeviceOptions({
    assetId: device?.assetId ?? assetId,
    category: device?.category ?? "",
  });
  const { deviceId, deliveryMethod, pickup, chooseDeliveryMethod, selectPickup } =
    useReplacementDraftStore();
  const chosenDevice = deviceOptions.options.find((option) => option.id === deviceId);
  const isChosenDeviceLoading = replacedDevice.status === "loading" || deviceOptions.isLoading;
  const isChosenDeviceMissing = !isChosenDeviceLoading && !chosenDevice;
  const shipmentOptions = useShipmentOptions({
    make: chosenDevice?.make ?? "",
    model: chosenDevice?.model ?? "",
  });
  const choices = toDeliveryChoices(shipmentOptions.deliveryOptions);
  const hasDisplayableChoices = choices.pickupBuildings.length > 0 || choices.isShipAvailable;
  const shippingAddress = useShippingAddress();

  const chooseBuilding = (building: PickupBuilding) =>
    selectPickup({ deliveryMethod: building.deliveryMethod, buildingCode: building.buildingCode });

  const selectedBuilding = choices.pickupBuildings.find(
    (building) =>
      building.deliveryMethod === pickup?.deliveryMethod &&
      building.buildingCode === pickup?.buildingCode
  );

  /** Retries whichever request the step is stuck on: the replaced device, the device options, or ShipmentHound. */
  const refetch = (): void => {
    if (!device) {
      replacedDevice.refetch();
    } else if (!chosenDevice) {
      void deviceOptions.refetch();
    } else {
      void shipmentOptions.refetch();
    }
  };

  return {
    choices,
    deliveryMethod,
    selectedBuilding,
    isLoading: !isChosenDeviceMissing && !shipmentOptions.isSuccess && !shipmentOptions.isError,
    isError: isChosenDeviceMissing || shipmentOptions.isError,
    isEmpty: shipmentOptions.isSuccess && !hasDisplayableChoices,
    refetch,
    shippingAddress,
    chooseMethod: chooseDeliveryMethod,
    chooseBuilding,
  };
};
